// @flow
import ko from 'rgs-knockout';
import _ from 'lodash';
import type { Observable, PureComputed } from 'rgs-knockout';

import { JSableOmitPrivate } from 'jsable';

export class PaginationPageItemModel {
	text: number | string;
	value: ?number;
	disabled: ?boolean;
	hided: ?boolean;
	active: ?boolean;
	additionalCssClass: ?string;

	constructor (params: {
		text: number | string,
		value: ?number,
		disabled: ?boolean,
		hided: ?boolean,
		active: ?boolean,
		additionalCssClass: ?boolean
	} = {
		text: '',
	}) {
		this.text = params.text;
		this.value = params.value;
		this.disabled = params.disabled;
		this.hided = params.hided;
		this.active = params.active;
		this.additionalCssClass = params.additionalCssClass;
	}
}

export type PaginationModelParams = {
	take?: number;
	currentPage?: number;
	visiblePagesInStart?: number;
	visiblePagesInEnd?: number;
	visiblePagesInMiddle?: number;
}


export class PaginationModel extends JSableOmitPrivate {
	take: Observable<number>;
	from: Observable<number>;
	Total: Observable<number>;
	pagesAmount: Observable<number>;
	pages: PureComputed<Array<PaginationPageItemModel>>;
	_currentPage: Observable<number>;

	_visiblePagesInStart: Observable<number>;
	_visiblePagesInEnd: Observable<number>;
	_visiblePagesInMiddle: Observable<number>;

	_firstVisibleIndex: PureComputed<number>;
	_lastVisibleIndex: PureComputed<number>;

	constructor (params: PaginationModelParams = {}) {
		super();

		params = _.defaultsDeep({}, params, {
			take: 20,
			currentPage: 1,
			visiblePagesInStart: 5,
			visiblePagesInMiddle: 3,
			visiblePagesInEnd: 5,
		});

		this._params = params;

		this.take = ko.observable(this._params.take)
			.extend({toJS: true});

		this.Total = ko.observable(0)
			.extend({fromJS: true});

		this.hidePaginationBlock = ko.pureComputed(() => this.pagesAmount() <= 1);
		
		this._pageBeforeCurrent = ko.observable(this._params.currentPage);

		this._currentPage = ko.map(ko.observable(this._params.currentPage), {
			mapRead: _.identity,
			mapWrite: (v: number): number => {
				this._pageBeforeCurrent(this._currentPage());
				return v;
			},
		});

		this.hasPaginationChanges = ko.pureComputed(() =>
			this._pageBeforeCurrent() !== this._currentPage()
		);

		this._visiblePagesInStart = ko.observable(this._params.visiblePagesInStart);
		this._visiblePagesInMiddle = ko.observable(this._params.visiblePagesInMiddle);
		this._visiblePagesInEnd = ko.observable(this._params.visiblePagesInEnd);

		this.from = ko.pureComputed(() => {
			return (this._currentPage() - 1) * this.take();
		})
		.extend({toJS: true});

		this.pagesAmount = ko.pureComputed(() => {
			return Math.ceil(this.Total() / this.take()) || 1;
		});

		this._firstVisibleIndex = ko.pureComputed(() => {
			const currentPage: number = this._currentPage();
			const pagesAmount: number = this.pagesAmount();
			const visiblePagesInStart: number = this._visiblePagesInStart();
			const visiblePagesInMiddle: number = this._visiblePagesInMiddle();

			if (currentPage >= visiblePagesInStart ) {
				if (currentPage > pagesAmount - (visiblePagesInStart - 1)) {
					return pagesAmount - (visiblePagesInStart - 1);
				}

				return currentPage - Math.floor(visiblePagesInMiddle / 2);
			}

			return 1;
		});

		this._lastVisibleIndex = ko.pureComputed(() => {
			const currentPage = this._currentPage();
			const pagesAmount = this.pagesAmount();
			const visiblePagesInStart:number = this._visiblePagesInStart();
			const visiblePagesInMiddle:number = this._visiblePagesInMiddle();
			const visiblePagesInEnd:number = this._visiblePagesInEnd();

			if (currentPage <= pagesAmount - (visiblePagesInEnd - 1) ) {
				if (currentPage < visiblePagesInStart) {
					return visiblePagesInStart;
				}

				return currentPage + Math.floor(visiblePagesInMiddle / 2);
			}

			return pagesAmount;
		});


		this.pages = ko.pureComputed(() => {
			let result = [];
			const currentPage:number = this._currentPage();
			const pagesAmount:number = this.pagesAmount();
			const firstVisibleIndex:number = this._firstVisibleIndex();
			const lastVisibleIndex:number = this._lastVisibleIndex();

			const range = _.range(firstVisibleIndex, lastVisibleIndex + 1);

			result = result.concat([
				new PaginationPageItemModel({
					text: '',
					value: currentPage - 1,
					disabled: currentPage === 1,
					additionalCssClass: `nfd-icon nfd-icon-arrow-orange
						 nfd-icon-arrow nfd-icon-arrow-left`,
				}),

				new PaginationPageItemModel({
					text: '1',
					value: 1,
					hided: firstVisibleIndex < 2,
				}),

				new PaginationPageItemModel({
					text: '...',
					hided: currentPage - 1 <= 3,
					disabled: true,
				}),

				...range.map(value => new PaginationPageItemModel({
					text: value,
					value,
					active: value === currentPage && range.length !== 1,
					disabled: range.length === 1,
					additionalCssClass: 'filled',
				})),

				new PaginationPageItemModel({
					text: '...',
					hided: currentPage + 1 >= this.pagesAmount() - 2,
					disabled: true,
				}),

				new PaginationPageItemModel({
					text: pagesAmount,
					value: pagesAmount,
					hided: lastVisibleIndex > pagesAmount - 2,
				}),

				new PaginationPageItemModel({
					text: '',
					value: currentPage + 1,
					disabled: currentPage === this.pagesAmount(),
					additionalCssClass: 'nfd-icon nfd-icon-arrow-orange nfd-icon-arrow-right',
				}),
			]);

			return result;
		});
	}
}
